import { Fragment } from "react";

import { Heading, Text } from "@theme/components/ThemeUi";

import type { HeadingProps } from "@theme/components/ThemeUi";
import type * as textVariants from "@theme/themes/ms5/variants/text";
import type { FC, ReactNode } from "react";

export interface ProductTitleProps extends HeadingProps {
  variant?: keyof typeof textVariants;
  children: string | ReactNode;
  isGoldFoil?: boolean;
  isGoldGradient?: boolean;
  styledThe?: boolean;
}

export const ProductTitle: FC<ProductTitleProps> = ({
  variant,
  children,
  isGoldFoil = false,
  isGoldGradient = false,
  styledThe = true,
  sx,
  className,
  ...props
}) => {
  // if children = ReactNode return with whatever styling is provided
  if (typeof children !== "string") {
    return (
      <Heading
        as={props.as ?? "h2"}
        variant={variant ?? "brandTitle04"}
        className={className}
        sx={{ color: "grey700", ...sx }}
        {...props}
      >
        {children}
      </Heading>
    );
  } else {
    // Normalize NBSP → regular space so CMS strings like "Mower® Ultra" split correctly
    const normalizedChildren = children.replace(/ /g, " ");
    // Check if the string has a digit dot digit, followed by Ultra (ex: 5.0 Ultra)
    // Needed to filter out Ultra Smooth
    const isGoldUltraRegex = new RegExp("\\d\\.\\d Ultra");
    const isGoldUltra = isGoldUltraRegex.test(normalizedChildren);
    // Word boundary so titles like "Hydrogen 3" don't match (ex: Gen 6, Ultra Gen 6)
    const isGoldGen = /\bGen\s+\d+\b/i.test(normalizedChildren);
    const words = normalizedChildren.split(" ");
    const StyledText = words.map((substring, index) => {
      const displayText = index === 0 ? `${substring}` : ` ${substring}`;
      const isGold = isGoldFoil || isGoldGradient;

      // Make "Gen N" gold, along with a directly preceding "Ultra" (ex: ULTRA GEN 6)
      const isGenWord = substring.toLowerCase() === "gen";
      const isGenNumber = /^\d+$/.test(substring) && words[index - 1]?.toLowerCase() === "gen";
      const isGenUltra =
        substring.toLowerCase() === "ultra" && words[index + 1]?.toLowerCase() === "gen";

      if (isGoldGen && (isGenWord || isGenNumber || isGenUltra)) {
        // NBSP before the number keeps "GEN 6" together without moving spaces inside spans
        const genText = isGenNumber ? `\u00A0${substring}` : substring;
        return (
          <Fragment key={index}>
            {!isGenNumber && index > 0 && " "}
            <Text sx={{ font: "inherit", color: "goldPrimary" }}>{genText}</Text>
          </Fragment>
        );
      }

      // Make `the` smaller than rest of the title text
      if (substring.toLowerCase() === "the") {
        return (
          <Text
            key={index}
            variant="brandTitle03"
            className="styled-the"
            sx={{
              font: "inherit",
              display: styledThe ? ["inline", null, "block"] : "inline",
              fontSize: styledThe ? ["fs44", null, "fs30"] : "inherit",
            }}
          >
            {displayText}
          </Text>
        );
      }
      // Make Ultra Gold if it is a numbered product with Ultra
      if (substring.toLowerCase() === "ultra" && isGoldUltra) {
        const withoutUltra = substring.replace(/ultra/gi, "");
        const goldVariant = isGoldFoil ? "goldFoil" : "goldProductGradient";

        return withoutUltra ? (
          <>
            <Text
              sx={{
                font: "inherit",
                color: "goldPrimary",
              }}
            >
              Ultra
            </Text>
            {withoutUltra}
          </>
        ) : (
          <Text
            variant={isGold ? goldVariant : undefined}
            sx={{
              font: "inherit",
              color: "goldPrimary",
            }}
          >
            {`\u00A0${substring}`}
          </Text>
        );
      }
      // if (number . number) i.e. 3.0 or 4.0, make them primary/goldFoil
      if (/\d*\.\d/.test(substring)) {
        // 5.0 includes "ultra" which we need to allow to break to new line
        const numberText = substring === "5.0" ? ` ${substring}` : `\u00A0${substring}`;

        return isGold ? (
          <Text
            className="styled-product-number"
            variant={isGoldFoil ? "goldFoil" : "goldProductGradient"}
            sx={{ font: "inherit", letterSpacing: "tight400" }}
          >
            {numberText}
          </Text>
        ) : (
          <Text
            className="styled-product-number"
            sx={{
              font: "inherit",
              color: "goldPrimary",
              letterSpacing: "tight400",
            }}
          >
            {numberText}
          </Text>
        );
      } else if (/\+/.test(substring)) {
        const plusText = index === 0 ? `${substring}` : ` ${substring}`;

        return (
          <Text
            key={index}
            sx={{
              font: "inherit",
              color: "goldPrimary",
            }}
          >
            {plusText}
          </Text>
        );
      } else return displayText;
    });

    return (
      <Heading
        as={props.as ?? "h2"}
        variant={variant ?? "brandTitle04"}
        className={className}
        {...props}
        sx={{ color: "grey700", ...sx }}
      >
        {StyledText.map((text, index) => (
          <Fragment key={index}>{text}</Fragment>
        ))}
      </Heading>
    );
  }
};
